/**
 * artifactCache — the CONTENT-ADDRESSED artifact body store (IndexedDB, no
 * deps; sweep contract §4/§2).
 *
 * The channel persists every successfully DOWNLOADED artifact body keyed by its
 * machine id and stamped with the content FINGERPRINT of the seed identity
 * that fetched it. The cache serves on a fingerprint MATCH (memory first, then
 * here) — a holder of identical content makes ZERO download calls; a
 * fingerprint miss makes exactly ONE download. It also remains the DESIGNED
 * offline path: when a fresh download fails (offline / network), the shell
 * serves the last downloaded body — loudly logged, never silently — and
 * rethrows when there is no cached body.
 */

const DB_NAME = "causit-artifact-cache";
const DB_VERSION = 1;
const STORE = "artifacts";

export type CachedArtifact = {
  /** artifact machine id — the cache key */
  machine: string;
  /** the downloaded artifact body, as served */
  artifact: unknown;
  /** the served artifact's machine.version at save time */
  version: string;
  /** the content fingerprint of the seed identity this body was fetched for
   * (shape/version identity today; the served face body fingerprint once the
   * world serves it — see useChannel artifactSeedFingerprintOf) */
  fingerprint?: string;
  /** the download response's sibling artifact bearer, when served (stored, not enforced yet) */
  artifactToken?: string;
  /** ISO timestamp of the save */
  savedAt: string;
};

const openDb = (): Promise<IDBDatabase> =>
  new Promise((resolve, reject) => {
    if (typeof indexedDB === "undefined") {
      reject(new Error("artifact cache unavailable: indexedDB is not supported in this context"));
      return;
    }
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) db.createObjectStore(STORE, { keyPath: "machine" });
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error ?? new Error("artifact cache open failed"));
  });

const tx = async <T>(mode: IDBTransactionMode, run: (store: IDBObjectStore) => IDBRequest<T>): Promise<T> => {
  const db = await openDb();
  try {
    return await new Promise<T>((resolve, reject) => {
      const req = run(db.transaction(STORE, mode).objectStore(STORE));
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => reject(req.error ?? new Error("artifact cache request failed"));
    });
  } finally {
    db.close();
  }
};

/** Write-through after a successful download. */
export const putCachedArtifact = async (record: CachedArtifact): Promise<void> => {
  if (!record.machine) throw new Error("artifact cache put requires the artifact machine id");
  await tx("readwrite", (store) => store.put(record));
};

/** The last downloaded body for a machine, or undefined when never cached. */
export const getCachedArtifact = async (machine: string): Promise<CachedArtifact | undefined> => {
  if (!machine) return undefined;
  const value = await tx<unknown>("readonly", (store) => store.get(machine) as IDBRequest<unknown>);
  return value && typeof value === "object" ? value as CachedArtifact : undefined;
};
